import React from "react";
import Styles from "./CtaHeader.module.scss";
import Icon from "@/shared/components/Icon/Icon";
import { useTheme } from "@/shared/styles/useTheme";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { sortCreatedDate } from "@/utils/utils";
import { baseMissingDataRoute, csFormPageStormUrl, csFormPageGasUrl } from "@/routes/changeservice";

export function isMissingData(cs: Remote.ChangeService) {
  return cs.hasMissingData;
}

type Props = {
  className?: string;
  changeServices: Remote.ChangeService[] | null;
};

export const CtaHeader = ({ className, changeServices }: Props) => {
  const { t } = useTranslation();
  const { primaryColorBorder, warningBackground } = useTheme();
  const orderedChangeServices = changeServices ? [...changeServices]?.sort(sortCreatedDate) : [];
  const hasMissingData = changeServices?.some(isMissingData);
  const firstCSinArrayWithMissingData = changeServices?.find(isMissingData);

  const changeService = orderedChangeServices.length
    ? orderedChangeServices.find((ch) => ch.id === firstCSinArrayWithMissingData?.id)
    : undefined;

  const categoryName = changeService?.category === "GAS" ? "Gas" : "Strom";

  const missingDataDetails = firstCSinArrayWithMissingData?.missingDataDetails
    ? JSON.parse(firstCSinArrayWithMissingData?.missingDataDetails)
    : undefined;

  const missingFields = missingDataDetails
    ? // eslint-disable-next-line
      // @ts-ignore
      missingDataDetails.sectionDetails.find((detail) => {
        return detail?.missingAttributes.length > 0;
      })
    : undefined;

  const hasElectricityChangeServices = changeServices?.filter((ch) => ch.category === "ELECTRICITY").length !== 0;
  const hasGasChangeServices = changeServices?.filter((ch) => ch.category === "GAS").length !== 0;

  let ctaURL = "";
  let currentCategory = "";
  if (hasMissingData) {
    currentCategory = categoryName && categoryName.toLowerCase();
    if (changeService) ctaURL = `${baseMissingDataRoute}/${currentCategory}/${changeService.id}`;
  } else {
    if (!hasElectricityChangeServices) {
      currentCategory = "Strom";
      ctaURL = csFormPageStormUrl;
    }
    if (!hasGasChangeServices) {
      currentCategory = "Gas";
      ctaURL = csFormPageGasUrl;
    }
    if (!hasElectricityChangeServices && !hasGasChangeServices) {
      ctaURL = csFormPageStormUrl;
      currentCategory = "Strom";
    }
  }

  if (!hasMissingData && hasElectricityChangeServices && hasGasChangeServices) return null;

  const notificationTitle = hasMissingData
    ? `${categoryName}-${t("dashboard.CSWithmissingData")}`
    : !hasElectricityChangeServices || !hasGasChangeServices
    ? t("dashboard.notificationNoChangeServices", {
        categoryName: currentCategory,
      })
    : null;

  const notificationSubheader = hasMissingData ? (
    <p>
      <b>
        {changeService?.street} {changeService?.streetNumber}
      </b>{" "}
      {t("dashboard.ctaHeaderMissing")}: <b>{missingFields?.missingAttributes?.join(", ")}</b>
    </p>
  ) : (
    (!hasElectricityChangeServices || !hasGasChangeServices) && <p>{t("dashboard.noChangeServices")}</p>
  );

  const buttonText = hasMissingData
    ? t("dashboard.completeData")
    : !hasElectricityChangeServices || !hasGasChangeServices
    ? t("dashboard.createChangeService")
    : t("dashboard.completeChangeService");

  return (
    <div className={classNames(Styles.ctaWrapper, primaryColorBorder, className)}>
      <div className={Styles.left}>
        <h2 className={classNames(Styles.header, "oswald")}>{notificationTitle}</h2>
        <p className={Styles.ctaSubHeader}>{notificationSubheader}</p>
      </div>
      <div className={Styles.right}>
        <Link to={ctaURL} className={classNames(Styles.button, warningBackground)}>
          <b className={Styles.btnContent}>{buttonText}</b>
          <Icon className={classNames(Styles.arrow)} name={"arrowRight"} />
        </Link>
      </div>
    </div>
  );
};
